import React from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/utils";

import { Period, TimePickerType, getArrowByType, getDateByType, setDateByType } from "./utils";

export interface TimePickerInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    picker: TimePickerType;
    date: Date | undefined;
    setDate: (date: Date | undefined) => void;
    period?: Period;
    onRightFocus?: () => void;
    onLeftFocus?: () => void;
}

const TimePickerInput = React.forwardRef<HTMLInputElement, TimePickerInputProps>(
    (
        {
            className,
            type = "tel",
            value,
            id,
            name,
            date = new Date(new Date().setHours(0, 0, 0, 0)),
            setDate,
            onChange,
            onKeyDown,
            picker,
            period,
            onLeftFocus,
            onRightFocus,
            ...props
        },
        ref
    ) => {
        const [flag, setFlag] = React.useState<number>(0);
        const [prevIntKey, setPrevIntKey] = React.useState<string>("0");

        const flagMax = picker === "milliseconds" ? 2 : 1;

        /**
         * allow the user to enter the second digit within 2 seconds
         * otherwise start again with entering first digit
         */
        React.useEffect(() => {
            if (flag > 0) {
                const timer = setTimeout(() => {
                    setFlag(0);
                }, 2000);

                return () => clearTimeout(timer);
            }
        }, [flag]);

        const calculatedValue = React.useMemo(() => {
            return getDateByType(date, picker);
        }, [date, picker]);

        const calculateNewValue = (key: string) => {
            /*
             * If picker is '12hours' and the first digit is 0, then the second digit is automatically set to 1.
             * The second entered digit will break the condition and the value will be set to 10-12.
             */
            if (picker === "12hours") {
                if (flag && calculatedValue.slice(1, 2) === "1" && prevIntKey === "0")
                    return "0" + key;
            }

            if (picker === "milliseconds") {
                if (flag === 0) return "00" + key;
                if (flag === 1) return "0" + (calculatedValue.slice(2, 3) + key);
                if (flag === 2) return calculatedValue.slice(1, 3) + key;
                throw new Error(`Invalid flag value, must be 0, 1 or 2. Is ${flag}`);
            } else {
                return flag === 0 ? "0" + key : calculatedValue.slice(1, 2) + key;
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Tab") return;
            e.preventDefault();
            if (e.key === "ArrowRight") onRightFocus?.();
            if (e.key === "ArrowLeft") onLeftFocus?.();
            if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                const step = e.key === "ArrowUp" ? 1 : -1;
                const newValue = getArrowByType(calculatedValue, step, picker);
                setFlag(0);
                const tempDate = new Date(date);
                setDate(setDateByType(tempDate, newValue, picker, period));
            }
            if (e.key >= "0" && e.key <= "9") {
                if (picker === "12hours") setPrevIntKey(e.key);

                const newValue = calculateNewValue(e.key);
                if (flag === flagMax) onRightFocus?.();
                setFlag(prev => {
                    if (prev == flagMax) {
                        return 0;
                    } else {
                        return prev + 1;
                    }
                });
                const tempDate = new Date(date);
                setDate(setDateByType(tempDate, newValue, picker, period));
            }
        };

        return (
            <Input
                ref={ref}
                id={id || picker}
                name={name || picker}
                className={cn(
                    "focus:bg-accent focus:text-accent-foreground w-[58px] text-center font-mono text-base tabular-nums caret-transparent [&::-webkit-inner-spin-button]:appearance-none",
                    className
                )}
                value={value || calculatedValue}
                onChange={e => {
                    e.preventDefault();
                    onChange?.(e);
                }}
                type={type}
                inputMode="decimal"
                onKeyDown={e => {
                    onKeyDown?.(e);
                    handleKeyDown(e);
                }}
                {...props}
            />
        );
    }
);

TimePickerInput.displayName = "TimePickerInput";

export { TimePickerInput };
